import './style.css';
import { calculateEstimate, formatRange } from './estimator';
import { qualifyLead } from './qualification';
import type { BudgetBand, CurrentState, EstimateInput, EstimateResult, Ownership, ProjectType, Quality, QualificationResult, Region, StartWindow } from './model';
import { clearAnalyticsEvents, getAnalyticsEvents, trackEvent } from './analytics';

const LEADS_KEY = 'reformas_leads_submissions_v1';
let lastInput: EstimateInput | null = null;
let lastEstimate: EstimateResult | null = null;
let lastQualification: QualificationResult | null = null;
let started = false;

const estimateForm = el<HTMLFormElement>('estimate-form');
const leadForm = el<HTMLFormElement>('lead-form');
const showLead = el<HTMLButtonElement>('show-lead-form');

estimateForm.addEventListener('input', () => {
  if (!started) { started = true; trackEvent('estimator_started'); }
});

estimateForm.addEventListener('submit', (ev) => {
  ev.preventDefault();
  el('form-errors').textContent = '';
  try {
    const input = readEstimate(new FormData(estimateForm));
    const estimate = calculateEstimate(input);
    const q = qualifyLead(input, estimate, { hasContact: false, privacyAccepted: false, shareAccepted: false, visitRequested: true });
    lastInput = input; lastEstimate = estimate; lastQualification = q;
    renderEstimate(input, estimate, q);
    trackEvent('estimate_generated', { projectType: input.projectType, region: input.region, quality: input.quality, currentState: input.currentState, startWindow: input.startWindow, ownership: input.ownership, budgetBand: input.budgetBand, estimateMin: estimate.min, estimateMax: estimate.max, leadScorePreview: q.score, leadTierPreview: q.tier });
  } catch (e) {
    el('form-errors').textContent = e instanceof Error ? e.message : 'No se pudo calcular.';
  }
});

showLead.addEventListener('click', () => {
  leadForm.classList.remove('hidden');
  showLead.classList.add('hidden');
  trackEvent('lead_form_viewed', lastQualification ? { score: lastQualification.score, tier: lastQualification.tier } : {});
  leadForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

leadForm.addEventListener('submit', (ev) => {
  ev.preventDefault();
  el('lead-errors').textContent = '';
  if (!lastInput || !lastEstimate) { el('lead-errors').textContent = 'Primero calcula una estimación.'; return; }
  const data = new FormData(leadForm);
  const email = String(data.get('email') ?? '').trim();
  const phone = String(data.get('phone') ?? '').trim();
  if (!email && !phone) { el('lead-errors').textContent = 'Indica email o teléfono.'; return; }
  const privacyAccepted = data.get('privacyAccepted') === 'on';
  const shareAccepted = data.get('shareAccepted') === 'on';
  const visitRequested = data.get('visitRequested') === 'on';
  if (!privacyAccepted || !shareAccepted) { el('lead-errors').textContent = 'Acepta privacidad y autorización.'; return; }

  const q = qualifyLead(lastInput, lastEstimate, { hasContact: true, privacyAccepted, shareAccepted, visitRequested });
  lastQualification = q;
  renderQualification(q);
  const lead = {
    id: createId('lead'), createdAt: new Date().toISOString(),
    estimateInput: { ...lastInput, postalCode: `${lastInput.postalCode.slice(0, 2)}***` },
    estimate: lastEstimate, qualification: q,
    contact: { name: String(data.get('name') ?? '').trim(), email, phone, preferredContact: String(data.get('preferredContact') ?? 'phone') },
    notes: String(data.get('notes') ?? '').trim(),
    consent: { privacyAccepted, shareAccepted, visitRequested },
    lifecycle: { status: 'new_demo_local_only', next: q.tier === 'alta' ? 'Llamar en menos de 2 horas y asignar 1-2 proveedores.' : 'Pedir más datos o nutrir antes de vender.' }
  };
  const leads = readLeads(); leads.push(lead); localStorage.setItem(LEADS_KEY, JSON.stringify(leads.slice(-50)));
  trackEvent('lead_submitted', { projectType: lastInput.projectType, region: lastInput.region, estimateMin: lastEstimate.min, estimateMax: lastEstimate.max, leadScore: q.score, leadTier: q.tier, budgetFit: q.budgetFit, hasEmail: Boolean(email), hasPhone: Boolean(phone) });
  leadForm.classList.add('hidden');
  el('lead-success').classList.remove('hidden');
});

el('export-analytics').addEventListener('click', () => {
  trackEvent('provider_value_viewed', { surface: 'privacy_export' });
  download('analytics-demo.json', JSON.stringify(getAnalyticsEvents(), null, 2));
});
el('clear-demo-data').addEventListener('click', () => {
  localStorage.removeItem(LEADS_KEY);
  clearAnalyticsEvents();
  trackEvent('demo_data_cleared');
  el('lead-success').classList.add('hidden');
});

function readEstimate(data: FormData): EstimateInput {
  return {
    projectType: read<ProjectType>(data, 'projectType'),
    areaSqm: Number(data.get('areaSqm')),
    postalCode: String(data.get('postalCode') ?? '').trim(),
    region: read<Region>(data, 'region'),
    quality: read<Quality>(data, 'quality'),
    currentState: read<CurrentState>(data, 'currentState'),
    bathrooms: Number(data.get('bathrooms') ?? 0),
    kitchenIncluded: data.get('kitchenIncluded') === 'yes',
    changesDistribution: data.get('changesDistribution') === 'on',
    changesInstallations: data.get('changesInstallations') === 'on',
    needsPermit: data.get('needsPermit') === 'on',
    startWindow: read<StartWindow>(data, 'startWindow'),
    ownership: read<Ownership>(data, 'ownership'),
    budgetBand: read<BudgetBand>(data, 'budgetBand')
  };
}

function renderEstimate(input: EstimateInput, estimate: EstimateResult, q: QualificationResult): void {
  el('empty').classList.add('hidden');
  el('estimate-result').classList.remove('hidden');
  showLead.classList.remove('hidden');
  leadForm.classList.add('hidden');
  el('estimate-range').textContent = formatRange(estimate.min, estimate.max);
  el('estimate-summary').textContent = `${estimate.label}; confianza ${estimate.confidence}. ${estimate.vatNote}`;
  list('assumptions', [...estimate.assumptions, 'No sustituye medición, memoria de calidades ni visita.', `CP usado para zona: ${input.postalCode.slice(0, 2)}***.`]);
  list('risks', estimate.risks);
  renderQualification(q);
}

function renderQualification(q: QualificationResult): void {
  el('lead-score').textContent = `${q.score}/100`;
  el('lead-tier').textContent = q.tier.toUpperCase();
  el('lead-value').textContent = `${formatRange(q.providerLeadValue.sharedMin, q.providerLeadValue.sharedMax)} compartido · ${formatRange(q.providerLeadValue.exclusiveMin, q.providerLeadValue.exclusiveMax)} exclusivo`;
}

function list(id: string, items: string[]): void {
  const ul = el<HTMLUListElement>(id); ul.innerHTML = '';
  for (const text of items) { const li = document.createElement('li'); li.textContent = text; ul.append(li); }
}
function readLeads(): unknown[] { try { const parsed = JSON.parse(localStorage.getItem(LEADS_KEY) ?? '[]') as unknown[]; return Array.isArray(parsed) ? parsed : []; } catch { return []; } }
function download(name: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}
function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}
function read<T extends string>(data: FormData, key: string): T { return String(data.get(key) ?? '') as T; }
function createId(prefix: string): string { return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Date.now().toString(16)}`; }
